import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator } from '@angular/forms';
import { generateClassesArray } from '@emma-helpers/emma-utils';
import { noop } from 'lodash';

import { EMMAFormElementComponent } from './emma-form-element.component';
import { SelectOption } from '@platform/types';
import { getNextMultiple, getPrevMultiple } from './emma-time-picker.helper';

const EMMA_TIMEPICKER_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  // tslint:disable-next-line: no-use-before-declare
  useExisting: forwardRef(() => EMMATimePickerComponent),
  multi: true,
};
const EMMA_TIMEPICKER_VALIDATOR: any = {
  provide: NG_VALIDATORS,
  // tslint:disable-next-line: no-use-before-declare
  useExisting: forwardRef(() => EMMATimePickerComponent),
  multi: true,
};

@Component({
    selector: 'emma-time-picker',
    templateUrl: './emma-time-picker.component.html',
    providers: [
        EMMA_TIMEPICKER_CONTROL_VALUE_ACCESSOR,
        EMMA_TIMEPICKER_VALIDATOR,
        {
            provide: EMMAFormElementComponent,
            useExisting: EMMATimePickerComponent,
        },
    ],
    standalone: false
})
export class EMMATimePickerComponent
  extends EMMAFormElementComponent
  implements OnInit, OnChanges, ControlValueAccessor, Validator
{
  // IN
  @Input() get time() {
    return this._time;
  }
  set time(time: string) {
    this.writeValue(time);
  }
  _time = '';
  hours?: number;
  minutes?: number;
  seconds?: number;

  // OUT
  @Output() override $change = new EventEmitter<string>();
  @Output() override $blur = new EventEmitter<string>();

  // options
  hoursOptions: Array<SelectOption | number> = [];
  minutesOptions: Array<SelectOption | number> = [];
  secondsOptions: Array<SelectOption | number> = [];

  @Input() hoursStep = 1;
  @Input() minutesStep = 5;
  @Input() secondsStep = 15;

  @Input() minTime?: string;
  minHours = 0;
  minMinutes = 0;
  minSeconds = 0;
  @Input() maxTime?: string;
  maxHours = 23;
  maxMinutes = 59;
  maxSeconds = 59;

  @Input() showHours = true;
  @Input() showMinutes = true;
  @Input() showSeconds = false;

  /** Icon */
  @Input() icon = 'calendar';

  /** Color */
  @Input() color?: string;

  /** I/O date format */
  @Input() timeFormat = 'HH:mm:ss';

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input() buttonClass = 'btn-metal';
  _buttonClass: Array<string> = [];

  _columnsShown = 1;

  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: string) => void = noop;

  public onChange(): void {
    if (this.hours !== undefined) {
      if (this.minutes === undefined) {
        this.minutes = this.minMinutes;
      }
    }
    if (this.minutes !== undefined) {
      if (this.seconds === undefined) {
        this.seconds = this.minSeconds;
      }
    }
    this.updateTime();
    this.updateOptions();
    this.$change.emit(this._time);
    this.onChangeCallback(this._time);
  }

  override updateClasses() {
    this._containerClass = generateClassesArray('emma-time-picker', this.containerClass);
    this._buttonClass = generateClassesArray('emma-time-picker--button m-btn btn', this.buttonClass);
    this._columnsShown =
      (this.showHours ? 1 : 0) +
      ((this.hours !== undefined || !this.showHours) && this.showMinutes ? 1 : 0) +
      ((this.minutes !== undefined || !this.showMinutes) && this.showSeconds ? 1 : 0);
  }

  private updateTime() {
    const hours = this.hours ? this.hours : 0;
    const minutes = this.minutes ? this.minutes : 0;
    const seconds = this.seconds ? this.seconds : 0;

    const hoursStr = hours < 10 ? `0${hours}` : hours.toString();
    const minutesStr = minutes < 10 ? `0${minutes}` : minutes.toString();
    const secondsStr = seconds < 10 ? `0${seconds}` : seconds.toString();
    this._time = `${hoursStr}:${minutesStr}:${secondsStr}`;
    this.updateClasses();
  }

  private updateValues() {
    const [hours, minutes, seconds] = this._time.split(':').map(Number);
    this.hours = !isNaN(hours) ? hours : this.minHours;
    this.minutes = !isNaN(minutes) ? minutes : this.minMinutes;
    this.seconds = !isNaN(seconds) ? seconds : this.minSeconds;
    this.updateClasses();
  }

  private updateLimits() {
    if (this.minTime) {
      const [hours, minutes, seconds] = this.minTime.split(':').map(Number);
      this.minHours = !isNaN(hours) ? getNextMultiple(hours, this.hoursStep) : 0;
      this.minMinutes = !isNaN(minutes) ? getNextMultiple(minutes, this.minutesStep) : 0;
      this.minSeconds = !isNaN(seconds) ? getNextMultiple(seconds, this.secondsStep) : 0;
    }
    if (this.maxTime) {
      const [hours, minutes, seconds] = this.maxTime.split(':').map(Number);
      this.maxHours = !isNaN(hours) ? getPrevMultiple(hours, this.hoursStep) : 23;
      this.maxMinutes = !isNaN(minutes) ? getPrevMultiple(minutes, this.minutesStep) : 59;
      this.maxSeconds = !isNaN(seconds) ? getPrevMultiple(seconds, this.secondsStep) : 59;
    }
  }

  private updateOptions() {
    this.hoursOptions = [];
    for (let value = this.minHours; value <= this.maxHours; value += this.hoursStep) {
      this.hoursOptions.push({
        label: value < 10 ? `0${value}` : value.toString(),
        value,
      });
    }
    const isMinHours = this.hours && this.hours <= this.minHours;
    const isMaxHours = this.hours && this.hours <= this.minHours;
    this.minutesOptions = [];
    for (
      let value = isMinHours ? this.minMinutes : 0;
      value <= (isMaxHours ? this.maxMinutes : 59);
      value += this.minutesStep
    ) {
      this.minutesOptions.push({
        label: value < 10 ? `0${value}` : value.toString(),
        value,
      });
    }
    const isMinMinutes = this.minutes && this.minutes <= this.minMinutes;
    const isMaxMinutes = this.minutes && this.minutes <= this.minMinutes;
    this.secondsOptions = [];
    for (
      let value = isMinHours && isMinMinutes ? this.minSeconds : 0;
      value <= (isMaxHours && isMaxMinutes ? this.maxSeconds : 59);
      value += this.secondsStep
    ) {
      this.secondsOptions.push({
        label: value < 10 ? `0${value}` : value.toString(),
        value,
      });
    }
  }

  onBlur() {
    this.onTouchedCallback();
    this.$blur.emit(this.time);
  }

  override ngOnInit(): void {
    this.updateOptions();
  }

  override ngOnChanges(changes: SimpleChanges): void {
    if (
      'minTime' in changes ||
      'maxTime' in changes ||
      'hoursStep' in changes ||
      'minutesStep' in changes ||
      'secondsStep' in changes
    ) {
      this.updateLimits();
    }
    if ('hours' in changes || 'minutes' in changes || 'seconds' in changes) {
      this.updateTime();
    }
    if (
      'minTime' in changes ||
      'maxTime' in changes ||
      'hoursStep' in changes ||
      'minutesStep' in changes ||
      'secondsStep' in changes
    ) {
      this.updateOptions();
    }
  }

  // From ControlValueAccessor interface
  writeValue(time: string | null) {
    if (time) {
      this._time = time;
      this.updateValues();
    }
  }

  // From ControlValueAccessor interface
  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  // From ControlValueAccessor interface
  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }
}
